import { IoT, DbApi } from "common-library/types"

export type Functionality = IoT.Functionality | "virtualFootfall" | "virtualPanorama"

export type Feature = DbApi.CameraNS.Feature

export type FeaturesMap = { [key in Feature]?: boolean }
export type DependencyMap = { [key in Feature]: Dependency }

/**
 * Feature Dependency
 */
class Dependency {
  conflicts: Dependency[] // OR COND
  depends: Dependency[] // AND COND
  dependsBy: Dependency[]

  feature: Feature
  functionality: Functionality[] // OR COND
  isMeta: boolean

  constructor(
    feature: Feature,
    functionalities: Functionality[],
    deps?: Dependency[],
    conflicts?: Dependency[]
  ) {
    this.depends = []
    this.conflicts = []
    this.dependsBy = []
    this.feature = feature
    this.functionality = functionalities
    this.isMeta = false

    this.addDep(...(deps ?? []))
    this.addConflict(...(conflicts ?? []))
  }

  addConflict(...conflicts: Dependency[]) {
    this.conflicts.push(...conflicts)
    for (const conflict of conflicts) {
      conflict.conflicts.push(this)
    }
  }

  addDep(...deps: Dependency[]) {
    for (const dep of deps) {
      dep.dependsBy.push(this)
    }
    this.depends.push(...deps)
  }

  private *iterInternal(key: "depends" | "conflicts" | "dependsBy") {
    const stk = [...this[key]]
    const seen = new Set<string>()

    while (stk.length > 0) {
      const dep = stk.pop()!
      if (seen.has(dep.feature)) {
        continue
      }
      yield dep
      seen.add(dep.feature)
      for (const ddep of dep[key]) {
        stk.push(ddep)
      }
    }
  }

  iterDepends() {
    return this.iterInternal("depends")
  }

  canEnable(functionalities: Set<Functionality>): boolean {
    function _canEnable(dep: Dependency) {
      return (
        dep.isMeta ||
        dep.functionality.length == 0 ||
        dep.functionality.some((f) => functionalities.has(f))
      )
    }

    let result = _canEnable(this)

    for (const depend of this.iterDepends()) {
      if (!result) {
        break
      }
      result = _canEnable(depend)
    }

    return result
  }

  markMeta() {
    this.isMeta = true
  }
}

export const VCA_EDGE_DEPS = createIPMDepsTree()
export const IPN_DEPS = createIPNDepsTree()

export function createIPNDepsTree(): DependencyMap {
  const depsMap = createDepsMap()
  let fk: Feature
  for (fk in depsMap) {
    if (
      depsMap[fk].functionality.includes("meta") ||
      depsMap[fk].functionality.includes("counter")
    ) {
      depsMap[fk].addConflict(depsMap.face)
    }
  }

  return depsMap
}

function createIPMDepsTree(): DependencyMap {
  const depsTree = createDepsMap()
  depsTree.rtFace.addDep(depsTree.edgeFace)

  depsTree.counter.markMeta()
  depsTree.face.markMeta()

  return depsTree
}

export function createDepsMap(): DependencyMap {
  const face = new Dependency("face", ["face"])
  const edgeFace = new Dependency("edgeFace", ["edgeFace"], [face])
  const counter = new Dependency("counter", ["counter", "virtualFootfall"])

  const metacounter = new Dependency("metacounter", ["meta"], [counter])
  const trackcounter = new Dependency("trackcounter", ["meta"], [counter])
  const groupFace = new Dependency("groupFace", ["edgeFace"], [edgeFace])
  const facecounter = new Dependency("facecounter", ["edgeFace"], [groupFace, counter])
  metacounter.addConflict(trackcounter, facecounter)
  trackcounter.addConflict(metacounter, facecounter)
  facecounter.addConflict(metacounter, trackcounter)

  const trackingtree = new Dependency("trackingtree", ["virtualPanorama"])
  const flowmap = new Dependency("flowmap", ["virtualPanorama"])
  const directionmap = new Dependency("directionmap", ["meta", "virtualPanorama"])
  const rtFace = new Dependency("rtFace", ["face", "edgeFace"], [face])
  const occupancy = new Dependency("occupancy", ["occupancy", "meta"])
  const zonetraffic = new Dependency("zonetraffic", ["meta"])

  const heatmap = new Dependency("heatmap", ["meta"])
  heatmap.markMeta()

  const trafficmap = new Dependency("trafficmap", ["meta"], [heatmap])
  const dwellmap = new Dependency("dwellmap", ["meta"])
  const heatmaps = new Dependency("heatmaps", [], [trafficmap, dwellmap])
  const zonetrace = new Dependency("zonetrace", ["meta"])
  const lci = new Dependency("lci", [])

  return {
    face,
    edgeFace,
    rtFace,
    occupancy,
    zonetraffic,
    groupFace,
    directionmap,
    flowmap,
    trackcounter,
    metacounter,
    counter,
    facecounter,
    trackingtree,
    heatmaps,
    heatmap,
    dwellmap,
    trafficmap,
    zonetrace,
    lci,
  }
}

export class FeaturesController {
  private depsMap: DependencyMap

  constructor(public features: FeaturesMap, backend?: IoT.FotaBackend) {
    if (backend === "vca_edge" || backend == "rva" || backend == "vca_server") {
      this.depsMap = VCA_EDGE_DEPS
    } else if (backend === "ipn") {
      this.depsMap = IPN_DEPS
    } else {
      this.depsMap = createDepsMap()
    }
  }

  getDependencies() {
    return Object.values(this.depsMap)
  }

  getDependency(feature: Feature) {
    return this.depsMap[feature]
  }

  canEnable(feature: Feature, functionalities: Set<Functionality>) {
    return this.depsMap[feature].canEnable(functionalities)
  }

  isMeta(feature: Feature) {
    return this.depsMap[feature].isMeta
  }

  disableUnsupportFeatures(functionalities: Set<Functionality>) {
    let feature: Feature
    for (feature in this.depsMap) {
      const dep = this.depsMap[feature]
      if (!dep.canEnable(functionalities)) {
        this.disableFeature(feature)
      }
    }

    for (feature in this.depsMap) {
      const dep = this.depsMap[feature]
      if (dep.isMeta) {
        if (
          dep.dependsBy.length == 0 ||
          dep.dependsBy.every((depBy) => !this.features[depBy.feature])
        ) {
          this.disableFeature(feature)
        }
      }
    }
  }

  disableFeature(feature: Feature) {
    if (!this.features[feature]) {
      return
    }

    const stk = [this.depsMap[feature]]
    const seen = new Set<string>()
    while (stk.length > 0) {
      const item = stk.pop()!
      if (seen.has(item.feature)) {
        continue
      }
      seen.add(item.feature)

      this.features[item.feature] = false

      for (const depend of item.depends) {
        if (depend.isMeta) {
          if (
            depend.dependsBy.length == 0 ||
            depend.dependsBy.every((depBy) => !this.features[depBy.feature])
          ) {
            stk.push(depend)
          }
        }
      }

      stk.push(...item.dependsBy)
    }
  }

  enableFeature(feature: Feature) {
    if (this.features[feature]) {
      return
    }

    const stk = [this.depsMap[feature]]
    const seen = new Set<string>()

    while (stk.length > 0) {
      const item = stk.pop()!
      if (seen.has(item.feature)) {
        continue
      }
      seen.add(item.feature)

      this.features[item.feature] = true

      for (const conflict of item.conflicts) {
        this.disableFeature(conflict.feature)
      }

      stk.push(...item.depends)
    }
  }

  toggleFeature(feature: Feature) {
    if (this.features[feature]) {
      return this.disableFeature(feature)
    } else {
      return this.enableFeature(feature)
    }
  }

  /**
   * 활성화된 모든 Feature를 사용할 수 있는 TrackEngine을 반환한다.
   */
  availableTrackingEngines(trackEngines: IoT.TrackingEngine.Data[]) {
    const enabledFeatures = (Object.keys(this.features) as Feature[]).filter(
      (feature) => !!this.features[feature]
    )

    return trackEngines.filter((te) =>
      enabledFeatures.every((dep) => this.depsMap[dep].canEnable(new Set(te.functionality)))
    )
  }
}

export class FunctionalitySet extends Set<Functionality> {
  private enableAll?: boolean

  constructor(model?: "footfall" | "panorama", iter?: Iterable<Functionality>) {
    super(iter)
    if (model == "panorama") {
      this.add("virtualPanorama")
    } else if (model == "footfall") {
      this.add("virtualFootfall")
    } else {
      this.enableAll = true
    }
  }

  override has(func: Functionality): boolean {
    return this.enableAll || super.has(func)
  }
}

interface Functionalities {
  license: Set<Functionality>
  device: Set<Functionality>
}

class DeviceFunctionalities implements Functionalities {
  public license: Set<Functionality>
  public device: Set<Functionality>
  private device_: IoT.Device.Data

  constructor(device: IoT.Device.Data) {
    this.device_ = device
    this.license = this.getLicenseFunctionalities()
    this.device = this.getDeviceFunctionalities()
  }

  private getLicenseFunctionalities() {
    const licenseFunctionalities = new Set<IoT.Functionality>()

    for (const license of this.device_.licenses) {
      license.functionality.forEach((f) => licenseFunctionalities.add(f))
    }

    return licenseFunctionalities
  }

  private getDeviceFunctionalities() {
    return (Object.keys(this.device_.description.functions) as IoT.Functionality[]).reduce(
      (functionalities, f) => {
        if (this.device_.description.functions[f]) {
          functionalities.add(f)
        }
        return functionalities
      },
      new Set<IoT.Functionality>()
    )
  }
}

export function createFunctionalities(
  device?: IoT.Device.Data,
  model?: "footfall" | "panorama"
): Functionalities {
  if (device) {
    return new DeviceFunctionalities(device)
  } else {
    return {
      license: new FunctionalitySet(model),
      device: new FunctionalitySet(model),
    }
  }
}
